import { Key, useState } from 'react'
import find from 'lodash/find'
import { observer } from 'mobx-react-lite'
import { generatePath, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useHover } from '@react-aria/interactions'

import Styles from './ProfileDropdown.module.scss'

import { NEW_WORKSPACE_PATH } from 'paths'
import { HELP_CENTER_URL } from 'utils/constants'

import { useModals } from 'almanac-ui/Modal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useSession } from 'hooks/useSession'
import { useDefaultWorkspacePath } from 'utils/useDefaultWorkspacePath'
import { useRootStore } from 'stores/RootStore'
import { useOpenSettings } from 'hooks/useOpenSettings'
import { useWorkspaceMembers } from 'hooks/useWorkspaceMembers'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'

import { getInitials } from 'utils'

import Avatar from 'common/Avatar/Avatar'
import { FlipperOverrideModal } from 'components/FlipperOverrideModal/FlipperOverrideModal'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { RootState, SidebarTabs, UserRole, Workspace } from 'types'
import { WorkspaceService } from 'api/services/WorkspaceService'
import { UserAppPreferencesService } from 'api/services/UserAppPreferencesService'
import { UserService } from 'api/clients/UserService'
import { ThemeMode } from 'stores/Theme.types'
import { Pressable } from '@react-aria/interactions'
import { SettingScreen } from 'components/Settings/types'
import { ReferralModalV2 } from 'components/Settings/ReferralModalV2/ReferralModalV2'
import { MainMenu } from './components/MainMenu/MainMenu'
import { WorkspacesMenu } from './components/WorkspacesMenu/WorkspacesMenu'
import { WorkspacesMenu as RedesignedWorkspacesMenu } from '../WorkspaceDropdown/WorkspacesMenu'
import { useReportBug } from 'components/BugReporter/useReportBug'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { ProfileDrawer } from './ProfileDrawer'
import { useTrialStatus } from 'hooks/useTrialStatus'
import { usePaywall } from 'components/TimeTriggerPaywall/hooks/usePaywall'
import { useQueryClient } from 'react-query'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import { updateModalsKey } from 'hooks/queries/updateModals/updateModalsKeys'
import { LiveSupportUpgradeModal } from 'components/LiveSupportUpgradeModal/LiveSupportUpgradeModal'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import {
  AUTOPILOT_COMMUNITY_LINK,
  COPILOT_COMMUNITY_LINK,
} from 'blaze-components/WorkspaceDashboardSidebar/components/More/components/BottomItems/BottomItems'
import { PurchaseContentCreditsModal } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/PurchaseContentCreditsModal/PurchaseContentCreditsModal'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { AutopilotPlansModal } from 'components/Settings/Workspace/AutopilotPlans/AutopilotPlansModal'
import { handleClearCacheAndRefresh } from 'components/Settings/SettingsModal'

export enum CurrentView {
  MainMenu = 'main_menu',
  SwitchWorkspace = 'switch_teams',
}

export interface ProfileDropdownProps {
  selectedWorkspaceId: number
  workspaces: Workspace[]
  skipTheme?: boolean
  hoveredBgColor?: string
  bgColor?: string
  avatarSize?: number
}

export const ProfileDropdown = observer(
  ({
    selectedWorkspaceId,
    workspaces,
    bgColor = 'var(--avatar)',
    hoveredBgColor = 'var(--selected-fill)',
    avatarSize,
  }: ProfileDropdownProps) => {
    const isSystemRedesignEnabled = useIsSystemRedesign()
    const history = useHistory()
    const { drawersStore } = useRootStore()
    const { auth, logout } = useSession()
    const { openModal } = useModals()
    const { isMobile } = useResponsiveDesign()
    const { openSettings } = useOpenSettings()
    const selectedWorkspace = find(workspaces, { id: selectedWorkspaceId })
    const { defaultWorkspacePath } = useDefaultWorkspacePath()
    const isSidebarOpen = useSelector((state: RootState) => state.sidebar.locked || state.floatingSidebar)
    const { workspaceTheme, tierUsageStore } = useRootStore()
    let { hoverProps, isHovered } = useHover({})
    const members = useWorkspaceMembers(selectedWorkspace?.key, { includeInactive: false })
    const [currentView, setCurrentView] = useState(CurrentView.MainMenu)
    const openWindowSafely = useOpenWindowSafely()
    const { reportBug } = useReportBug()
    const { setCurrentFolder } = useCurrentFolderStore()
    const { trialDaysLeft, isInTrial } = useTrialStatus()
    const { openPaywall } = usePaywall()
    const queryClient = useQueryClient()
    const { autopilotAddOns } = tierUsageStore
    const autopilotContentCreditsAddon = autopilotAddOns.find(
      (addon) => addon.key === PaymentPlanKey.BlazeAutopilotContentCredits,
    )
    const autopilotContentCreditsPrice = autopilotContentCreditsAddon?.prices[0]

    const communityLink = selectedWorkspace?.is_virtual_marketer ? AUTOPILOT_COMMUNITY_LINK : COPILOT_COMMUNITY_LINK

    const clearCalendarCache = () => {
      const queries = queryClient.getQueriesData<AlmanacFileCalendarItem[]>(calendarKeys.all)
      queries.forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, null)
        queryClient.cancelQueries(queryKey)
      })
    }

    const clearWorkspaceSpecificQueries = () => {
      clearCalendarCache()
      queryClient.invalidateQueries(updateModalsKey)
    }

    const profile = members?.find((member) => member.id === auth?.id)

    if (!selectedWorkspace) {
      return null
    }

    const handleAction = (key: Key, close: () => void) => {
      if (key === 'profile') {
        openSettings()
      }
      if (key === 'creditUsage' && autopilotContentCreditsPrice) {
        if (tierUsageStore.currentSubscription?.status === 'trialing') {
          openModal(AutopilotPlansModal)
        } else {
          openModal(PurchaseContentCreditsModal, { price: autopilotContentCreditsPrice })
        }
      }
      if (key === 'settings') {
        openSettings()
      }
      if (key === 'billing') {
        if (trialDaysLeft > 0 && isInTrial) {
          openPaywall()
        } else {
          openSettings(SettingScreen.Billing)
        }
      }
      if (key === 'switchWorkspace') {
        setCurrentView(CurrentView.SwitchWorkspace)
        return
      }
      if (key === 'joinCommunity') {
        openWindowSafely(communityLink)
      }
      if (key === 'helpCenter') {
        openWindowSafely(HELP_CENTER_URL)
      }
      if (key === 'bugReporter') {
        reportBug()
      }
      if (key === 'referAndEarn') {
        openModal(ReferralModalV2)
      }
      if (key === 'intercomControl') {
        if (selectedWorkspace?.is_ap_freemium) {
          openModal(LiveSupportUpgradeModal)
        }
        Intercom('show')
      }
      if (key.toString().startsWith('workspace')) {
        const destinationWorkspace = workspaces.find((w) => w.id === parseInt(key.toString().split('-')[1]))
        if (!destinationWorkspace) {
          return
        }
        UserAppPreferencesService.updateActiveTab(SidebarTabs.Organizational)
        UserService.setCurrentWorkspaceId(destinationWorkspace.id)
        setCurrentFolder(null)
        if (destinationWorkspace.is_new) {
          WorkspaceService.markAsSeen(destinationWorkspace.key)
        }
        clearWorkspaceSpecificQueries()
        history.push(
          defaultWorkspacePath({
            id: destinationWorkspace.id,
            guestReader: destinationWorkspace.role === UserRole.GUEST,
            homepageDocSlug: destinationWorkspace.homepage_doc_slug,
          }),
        )
      }
      if (key === 'newWorkspace') {
        history.push(generatePath(NEW_WORKSPACE_PATH))
      }
      if (key === 'releaseInfo') {
        handleClearCacheAndRefresh()
      }
      if (key === 'featureFlags') {
        openModal(FlipperOverrideModal)
      }
      if (key === 'signout') {
        logout()
      }
      if (isMobile && isSidebarOpen) WorkspaceService.hideSidebar()
      setCurrentView(CurrentView.MainMenu)
      close()
    }

    const avatarBackgroundColor = isHovered ? hoveredBgColor : bgColor
    const triggerSize = avatarSize || (isSystemRedesignEnabled ? 32 : 40)
    const workspaceMenuProps = {
      onBack: () => setCurrentView(CurrentView.MainMenu),
      workspaces: workspaces,
      workspaceId: selectedWorkspaceId,
    }

    const trigger = (
      <div className={Styles.trigger} data-testid="dropdownTriggerBtn" {...hoverProps}>
        {profile ? (
          <>
            {profile?.avatar_url ? (
              <img
                className={Styles.profileButton}
                style={{ height: triggerSize, width: triggerSize }}
                src={profile.avatar_url}
                alt="user profile"
              />
            ) : (
              <Avatar
                data-testid="avatar"
                size={triggerSize}
                label={getInitials(selectedWorkspace.name)}
                url={selectedWorkspace.logo_url}
                workspaceId={selectedWorkspace.id}
                backgroundColor={isSystemRedesignEnabled ? '#dfdfdf' : avatarBackgroundColor}
              />
            )}
          </>
        ) : (
          <img
            className={Styles.profileButton}
            style={{ height: triggerSize, width: triggerSize }}
            src={Silhouette}
            alt="user profile"
          />
        )}
      </div>
    )

    if (isMobile) {
      return (
        <Pressable
          onPress={() => {
            drawersStore.openDrawer(ProfileDrawer, { handleAction, selectedWorkspace, workspaces, profile })
          }}
        >
          {trigger}
        </Pressable>
      )
    }

    return (
      <DropdownTrigger
        isDisabled={!auth}
        placement="bottom-start"
        popoverClassName={Styles.popover}
        __UNSAFE_popoverStyle={{
          ...workspaceTheme.palette,
          boxShadow: workspaceTheme.mode === ThemeMode.Dark ? '0px 8px 24px 0px rgba(19, 21, 26, 0.40)' : undefined,
        }}
        onClose={() => setCurrentView(CurrentView.MainMenu)}
      >
        <Pressable>{trigger}</Pressable>
        {(close) => {
          if (currentView === CurrentView.SwitchWorkspace) {
            return (
              <>
                {isSystemRedesignEnabled ? (
                  <RedesignedWorkspacesMenu {...workspaceMenuProps} onAction={(key) => handleAction(key, close)} />
                ) : (
                  <WorkspacesMenu
                    {...workspaceMenuProps}
                    onAction={(key) => handleAction(key, close)}
                    onClose={close}
                  />
                )}
              </>
            )
          }

          return (
            <MainMenu onAction={(key) => handleAction(key, close)} workspace={selectedWorkspace} profile={profile} />
          )
        }}
      </DropdownTrigger>
    )
  },
)
